import React from 'react';
import { Divider, Spin } from 'antd';
import { PencilIcon } from '@/assets/icons/icons';
import { SavedDateList, SystemDatePresetSummary } from '@/apis/logiblocksAPI';
import LoadErrorNotice from './LoadErrorNotice';
import { presetDescriptor, savedListDescriptor } from './dateSetUtils';
import styles from './DateSetValueMenu.module.css';

interface DateSetValueMenuProps {
  presets: SystemDatePresetSummary[];
  savedLists: SavedDateList[];
  loading: boolean;
  /** Presets or saved lists failed to load. */
  loadFailed: boolean;
  onRetry: () => void;
  onSelectPreset: (preset: SystemDatePresetSummary) => void;
  onSelectSavedList: (list: SavedDateList) => void;
  onEditSavedList: (list: SavedDateList) => void;
  onSelectCustomDates: () => void;
}

/** The value menu: system presets, then saved lists (each with an edit
 * button), then Custom dates, grouped by plain dividers. */
const DateSetValueMenu: React.FC<DateSetValueMenuProps> = ({
  presets,
  savedLists,
  loading,
  loadFailed,
  onRetry,
  onSelectPreset,
  onSelectSavedList,
  onEditSavedList,
  onSelectCustomDates,
}) => {
  if (loading) {
    return (
      <div className={styles.menu}>
        <div className={styles.loading}>
          <Spin size='small' />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.menu}>
      {loadFailed && (
        <div className={styles.loadError}>
          <LoadErrorNotice
            message="Couldn't load presets and saved lists."
            onRetry={onRetry}
          />
        </div>
      )}

      {presets.map((preset) => (
        <button
          key={preset.preset_id}
          type='button'
          className={styles.row}
          onClick={() => onSelectPreset(preset)}
          title={
            preset.iana_timezone
              ? `Observed dates, evaluated in ${preset.iana_timezone}. Maintained by Zoomlogi.`
              : undefined
          }
        >
          <span className={styles.rowLabel}>{preset.display_name}</span>
          <span className={styles.rowCount}>({preset.count})</span>
          <span className={styles.rowMeta}>{presetDescriptor(preset)}</span>
        </button>
      ))}

      {savedLists.length > 0 && (
        <>
          <Divider className={styles.divider} />
          {savedLists.map((list) => (
            <div key={list.id} className={styles.listRow}>
              <button
                type='button'
                className={styles.row}
                onClick={() => onSelectSavedList(list)}
              >
                <span className={styles.rowLabel}>{list.name}</span>
                <span className={styles.rowCount}>({list.entry_count})</span>
                <span className={styles.rowMeta}>
                  {savedListDescriptor(list)}
                </span>
              </button>
              <button
                type='button'
                className={styles.editButton}
                aria-label={`Edit ${list.name}`}
                onClick={() => onEditSavedList(list)}
              >
                <PencilIcon color='currentColor' />
              </button>
            </div>
          ))}
        </>
      )}

      <Divider className={styles.divider} />
      <button
        type='button'
        className={styles.row}
        onClick={onSelectCustomDates}
      >
        <span className={styles.rowLabel}>Custom dates</span>
      </button>
    </div>
  );
};

export default DateSetValueMenu;
